import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Input,
  Stack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  addDays,
  differenceInCalendarDays,
  format,
  startOfDay,
} from "date-fns";
import { v4 as uuid } from "uuid";

import {
  getEmployeeWorkScheduleAction,
  updateEmployeeWorkScheduleAction,
} from "../../../../../actions/employeeActions";
import {
  EMPLOYEE_WORK_SCHEDULE_RESET,
  EMPLOYEE_WORK_SCHEDULE_UPDATE_RESET,
} from "../../../../../constants/employeeConstants";
import EmployeeEventCalendar from "../../../../../components/employee-calendar/employee-event-calendar";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { textSecondary } from "../../../../../utils/colors";
import { TbTrash } from "react-icons/tb";

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const DEFAULT_START = "08:00";
const DEFAULT_END = "17:00";

const buildDefaultDays = () =>
  DAY_ORDER.reduce((acc, day, index) => {
    const enabled = index < 5; // Monday-Friday enabled by default
    acc[day] = {
      enabled,
      start: DEFAULT_START,
      end: DEFAULT_END,
    };
    return acc;
  }, {});

const normaliseSegment = (segment = {}) => {
  const days = segment.days || {};
  const normalisedDays = DAY_ORDER.reduce((acc, day) => {
    const config = days[day] || {};
    acc[day] = {
      enabled: config.enabled === false ? false : true,
      start: typeof config.start === "string" ? config.start : DEFAULT_START,
      end: typeof config.end === "string" ? config.end : DEFAULT_END,
    };
    return acc;
  }, {});

  return {
    _id: segment._id || uuid(),
    startDate: segment.startDate ? segment.startDate : "",
    endDate: segment.endDate ? segment.endDate : "",
    timezone: segment.timezone || "",
    days: normalisedDays,
  };
};

const generateEventsFromSchedule = (segments = []) => {
  const events = [];

  segments.forEach((segment) => {
    if (!segment.startDate || !segment.endDate) return;
    const startDate = new Date(segment.startDate);
    const endDate = new Date(segment.endDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
      return;
    if (startDate > endDate) return;

    const totalDays = differenceInCalendarDays(endDate, startDate);
    // Guard against absurd ranges that could impact performance
    if (totalDays > 366) return;

    for (let offset = 0; offset <= totalDays; offset += 1) {
      const currentDate = addDays(startOfDay(startDate), offset);
      const dayKey =
        DAY_ORDER[currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1];
      const dayConfig = segment.days?.[dayKey];

      if (!dayConfig || dayConfig.enabled === false) continue;
      if (
        !TIME_PATTERN.test(dayConfig.start) ||
        !TIME_PATTERN.test(dayConfig.end)
      )
        continue;
      if (dayConfig.start >= dayConfig.end) continue;

      const [startHour, startMinute] = dayConfig.start.split(":").map(Number);
      const [endHour, endMinute] = dayConfig.end.split(":").map(Number);

      const eventStart = new Date(currentDate);
      eventStart.setHours(startHour, startMinute, 0, 0);
      const eventEnd = new Date(currentDate);
      eventEnd.setHours(endHour, endMinute, 0, 0);

      events.push({
        id: `${segment._id}-${eventStart.toISOString()}`,
        title: "Working hours",
        start: eventStart,
        end: eventEnd,
        color: "emerald",
        calendarId: "schedule",
      });
    }
  });

  return events;
};

const EmployeeWorkSchedule = ({
  slug,
  employee,
  employeeId,
  onScheduleUpdated,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const { backgroundLightMode, newBorderMode, whiteMode } = useThemeColors();

  const [segments, setSegments] = useState([]);
  const [form, setForm] = useState({
    startDate: "",
    endDate: "",
    days: buildDefaultDays(),
  });

  const workScheduleState = useSelector((state) => state.employeeWorkSchedule);
  const {
    loading: scheduleLoading,
    error: scheduleError,
    workSchedule,
  } = workScheduleState;

  const workScheduleUpdateState = useSelector(
    (state) => state.employeeWorkScheduleUpdate
  );
  const {
    loading: updateLoading,
    error: updateError,
    success: updateSuccess,
  } = workScheduleUpdateState;

  const tenantTimezone = employee?.timezone;

  useEffect(() => {
    dispatch({ type: EMPLOYEE_WORK_SCHEDULE_RESET });
    if (slug && employeeId) {
      dispatch(getEmployeeWorkScheduleAction(slug, employeeId));
    }
  }, [dispatch, slug, employeeId]);

  useEffect(() => {
    if (workSchedule && Array.isArray(workSchedule)) {
      setSegments(workSchedule.map(normaliseSegment));
    } else if (employee?.workSchedule) {
      setSegments(employee.workSchedule.map(normaliseSegment));
    }
  }, [workSchedule, employee?.workSchedule]);

  useEffect(() => {
    if (scheduleError) {
      toast({
        title: t("employee.workScheduleLoadError", "Unable to load schedule"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [scheduleError, toast, t]);

  useEffect(() => {
    if (updateSuccess) {
      toast({
        title: t("employee.workScheduleUpdate", "Work schedule updated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      dispatch({ type: EMPLOYEE_WORK_SCHEDULE_UPDATE_RESET });
      if (onScheduleUpdated) {
        onScheduleUpdated();
      }
      if (slug && employeeId) {
        dispatch(getEmployeeWorkScheduleAction(slug, employeeId));
      }
    } else if (updateError) {
      toast({
        title: t(
          "employee.workScheduleUpdateError",
          "Unable to update work schedule"
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      dispatch({ type: EMPLOYEE_WORK_SCHEDULE_UPDATE_RESET });
    }
  }, [
    updateSuccess,
    updateError,
    dispatch,
    toast,
    t,
    slug,
    employeeId,
    onScheduleUpdated,
  ]);

  const setDayEnabled = (day, enabled) => {
    setForm((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: {
          ...prev.days[day],
          enabled,
        },
      },
    }));
  };

  const setDayTime = (day, field, value) => {
    setForm((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: {
          ...prev.days[day],
          [field]: value,
        },
      },
    }));
  };

  const resetForm = () => {
    setForm({
      startDate: "",
      endDate: "",
      days: buildDefaultDays(),
    });
  };

  const handleAddSchedule = () => {
    if (!form.startDate || !form.endDate) {
      toast({
        title: t(
          "employee.workScheduleValidation",
          "Please select start and end dates"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const startDate = new Date(form.startDate);
    const endDate = new Date(form.endDate);
    if (startDate > endDate) {
      toast({
        title: t(
          "employee.workScheduleDateOrder",
          "Start date must be before end date"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const enabledDays = DAY_ORDER.filter((day) => form.days[day]?.enabled);
    if (enabledDays.length === 0) {
      toast({
        title: t(
          "employee.workScheduleNoDays",
          "Enable at least one working day"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    for (const day of enabledDays) {
      const config = form.days[day];
      if (!TIME_PATTERN.test(config.start) || !TIME_PATTERN.test(config.end)) {
        toast({
          title: t("employee.workScheduleInvalidTime", "Invalid time format"),
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (config.start >= config.end) {
        toast({
          title: t(
            "employee.workScheduleTimeOrder",
            "Start time must be before end time"
          ),
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    const newSegment = {
      _id: uuid(),
      startDate: form.startDate,
      endDate: form.endDate,
      timezone: tenantTimezone || undefined,
      days: buildDefaultDays(),
    };

    DAY_ORDER.forEach((day) => {
      newSegment.days[day] = { ...form.days[day] };
    });

    const nextSegments = [...segments, newSegment];
    setSegments(nextSegments);
    resetForm();
    dispatch(updateEmployeeWorkScheduleAction(slug, employeeId, nextSegments));
  };

  const handleRemoveSchedule = (segmentId) => {
    const nextSegments = segments.filter(
      (segment) => segment._id !== segmentId
    );
    setSegments(nextSegments);
    dispatch(updateEmployeeWorkScheduleAction(slug, employeeId, nextSegments));
  };

  const events = useMemo(
    () => generateEventsFromSchedule(segments),
    [segments]
  );

  return (
    <Box w="100%" mt="2rem">
      <Heading fontSize="1.125rem" fontWeight="600" mb={4}>
        {t("employee.workCalendar")}
      </Heading>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 6, md: 8 }}
        align="flex-start"
      >
        <Box
          flex="1"
          w="100%"
          bg={backgroundLightMode}
          p="1.5rem"
          borderRadius="1rem"
          border={`1px solid ${newBorderMode}`}
        >
          <Heading fontSize="1rem" fontWeight="600" mb={4}>
            {t("employee.createWorkPeriod", "Create work period")}
          </Heading>

          <Stack spacing={4}>
            <Flex gap={4} wrap="wrap">
              <Box flex="1" minW="180px">
                <Text fontSize="0.85rem" fontWeight="500" mb={1}>
                  {t("common.startDate", "Start date")}
                </Text>
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                />
              </Box>
              <Box flex="1" minW="180px">
                <Text fontSize="0.85rem" fontWeight="500" mb={1}>
                  {t("common.endDate", "End date")}
                </Text>
                <Input
                  type="date"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                />
              </Box>
            </Flex>

            <Divider />

            <Stack spacing={3}>
              {DAY_ORDER.map((day) => {
                const config = form.days[day];
                const label = t(`clinicSettings.days.${day}`, day);
                return (
                  <Flex
                    key={day}
                    align="center"
                    gap={4}
                    wrap="wrap"
                    border={`1px solid ${newBorderMode}`}
                    borderRadius="0.75rem"
                    p="0.75rem 1rem"
                    bg={config.enabled ? whiteMode : backgroundLightMode}
                  >
                    <Flex align="center" gap={3} flex="1 1 160px">
                      <Switch
                        isChecked={config.enabled}
                        onChange={(e) => setDayEnabled(day, e.target.checked)}
                      />
                      <Text fontWeight="600">{label}</Text>
                    </Flex>
                    <Flex align="center" gap={2} flex="1 1 180px">
                      <Text fontSize="0.8rem" color={textSecondary}>
                        {t("employee.start", "Start")}
                      </Text>
                      <Input
                        type="time"
                        value={config.start}
                        onChange={(e) =>
                          setDayTime(day, "start", e.target.value)
                        }
                        isDisabled={!config.enabled}
                      />
                    </Flex>
                    <Flex align="center" gap={2} flex="1 1 180px">
                      <Text fontSize="0.8rem" color={textSecondary}>
                        {t("employee.end", "End")}
                      </Text>
                      <Input
                        type="time"
                        value={config.end}
                        onChange={(e) => setDayTime(day, "end", e.target.value)}
                        isDisabled={!config.enabled}
                      />
                    </Flex>
                  </Flex>
                );
              })}
            </Stack>

            <Flex gap={3} justify="flex-end">
              <Button variant="ghost" onClick={resetForm}>
                {t("common.reset", "Reset")}
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleAddSchedule}
                isLoading={updateLoading}
              >
                {t("employee.addToSchedule", "Add to schedule")}
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Flex>

      <Box mt={8}>
        <Heading fontSize="1rem" fontWeight="600" mb={3}>
          {t("employee.activeWorkPeriods", "Active work periods")}
        </Heading>
        {segments.length === 0 ? (
          <Text color={textSecondary} fontSize="sm">
            {t(
              "employee.noWorkPeriods",
              "No work periods have been scheduled yet."
            )}
          </Text>
        ) : (
          <Stack spacing={3} mb={6}>
            {segments.map((segment) => (
              <Flex
                key={segment._id}
                align="center"
                justify="space-between"
                p="0.85rem 1rem"
                borderRadius="0.75rem"
                border={`1px solid ${newBorderMode}`}
                bg={backgroundLightMode}
                wrap="wrap"
                gap={3}
              >
                <Box>
                  <Text fontWeight="600">
                    {format(new Date(segment.startDate), "MMM d, yyyy")} –{" "}
                    {format(new Date(segment.endDate), "MMM d, yyyy")}
                  </Text>
                  <Text fontSize="0.8rem" color={textSecondary}>
                    {DAY_ORDER.filter((day) => segment.days[day]?.enabled)
                      .map((day) => t(`clinicSettings.days.${day}`, day))
                      .join(", ")}
                  </Text>
                </Box>
                <IconButton
                  aria-label={t("common.delete", "Delete")}
                  icon={<TbTrash />}
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemoveSchedule(segment._id)}
                  isLoading={updateLoading}
                />
              </Flex>
            ))}
          </Stack>
        )}
      </Box>

      <Box
        border={`1px solid ${newBorderMode}`}
        borderRadius="1rem"
        overflow="hidden"
        bg={backgroundLightMode}
        p={{ base: "1rem", md: "1.25rem" }}
        id="tw-root"
      >
        {scheduleLoading ? (
          <Flex align="center" justify="center" minH="240px">
            <Text color={textSecondary}>
              {t("common.loading", "Loading...")}
            </Text>
          </Flex>
        ) : (
          <EmployeeEventCalendar
            events={events}
            initialView="week"
            showResourceToggle={false}
            calendars={[]}
            onViewChange={() => {}}
            timezone={employee?.timezone}
            timeFormat={employee?.timeFormat || "24h"}
            weekStartsOn={1}
            businessHours={{ startHour: 8, endHour: 20 }}
            clinicChairs={1}
          />
        )}
      </Box>
    </Box>
  );
};

export default EmployeeWorkSchedule;
